import React, { useEffect, useState, useCallback } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Instagram, Loader2, Download, ShieldCheck } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";
import { useBandLabel } from "@/components/ScoreRadial";
import { buildCard, buildJourneyCard, buildScoreCard, shareCard, downloadCard } from "@/lib/shareImage";
import { journeyFromTimeline } from "@/lib/journey";
import api from "@/lib/api";
import { toast } from "sonner";

const TABS = [["post", "1080×1350"], ["story", "1080×1920"], ["journey", ""], ["score", ""]];

export default function ShareCardDialog({ open, onOpenChange, project, initialFormat = "post" }) {
  const { t, lang } = useLang();
  const bandLabel = useBandLabel();
  const [format, setFormat] = useState(initialFormat);
  useEffect(() => { if (open) setFormat(initialFormat); }, [open, initialFormat]);
  const [journeySize, setJourneySize] = useState("post");
  const [timeline, setTimeline] = useState(null);
  const [preview, setPreview] = useState(null);
  const [blob, setBlob] = useState(null);
  const [busy, setBusy] = useState(false);
  const [sharing, setSharing] = useState(false);
  const isJourney = format === "journey";
  const isScore = format === "score";
  const size = isJourney || isScore ? journeySize : format;

  useEffect(() => {
    if (open && isJourney && !timeline && project) api.get(`/projects/${project.id}/timeline`).then((r) => setTimeline(r.data.items || [])).catch(() => setTimeline([]));
  }, [open, isJourney, timeline, project]);

  const gen = useCallback(async () => {
    if (!project || (isJourney && !timeline)) return;
    setBusy(true);
    try {
      const maturity = t(`maturity.${project.maturity || "captured"}`);
      let out;
      if (isJourney) {
        const journey = journeyFromTimeline(timeline, t, lang);
        out = await buildJourneyCard(project, journey, journeySize, { title: t("share.journey_title"), maturity, days: t("share.j_days").replace("{n}", journey.days), score: t("share.card_score"), build: t("share.card_build") });
      } else if (isScore) {
        out = await buildScoreCard(project, journeySize, {
          label: t("cci.share_label"), headline: t("share.score_headline"), bandLabel: bandLabel(project.score?.band), note: t("share.score_note"), tagline: t("cci_show.tagline"),
          comp: { clarity: t("cci.comp.clarity"), evidence: t("cci.comp.evidence"), coherence: t("cci.comp.coherence"), validation: t("cci.comp.validation") },
        });
      } else {
        out = await buildCard(project, format, {
          myIdea: t("share.card_myidea"), score: t("share.card_score"), build: t("share.card_build"), stage: t("share.card_maturity"), maturity,
          bandLabel: bandLabel(project.score?.band), comp: { clarity: t("cci.comp.clarity"), evidence: t("cci.comp.evidence"), coherence: t("cci.comp.coherence"), validation: t("cci.comp.validation") },
          decisions: t("share.card_decisions").replace("{n}", Object.keys(project.answers || {}).length),
          evidence: t("share.card_evidence").replace("{n}", project.evidence_count ?? 0),
        });
      }
      setPreview(out.dataUrl); setBlob(out.blob);
    } catch (e) { toast.error("—"); }
    setBusy(false);
  }, [project, format, journeySize, isJourney, isScore, timeline, t, lang]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { if (open) gen(); }, [open, gen]);

  const doShare = async () => {
    if (!blob) return;
    setSharing(true);
    const res = await shareCard(project, blob, size);
    if (res === "download") toast.success(t("share.downloaded"));
    setSharing(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="!max-w-[440px] rounded-2xl max-h-[92vh] overflow-y-auto" data-testid="share-card-dialog">
        <DialogHeader>
          <DialogTitle>{t("share.open_share")}</DialogTitle>
          <DialogDescription>{t("share.body")}</DialogDescription>
        </DialogHeader>

        <div className="flex gap-2 justify-center flex-wrap" role="group" aria-label={t("share.format_label")}>
          {TABS.map(([f, sz]) => (
            <button key={f} type="button" data-testid={`share-format-${f}`} onClick={() => setFormat(f)} aria-pressed={format === f} aria-label={`${t(`share.${f}`)} ${sz}`.trim()}
              className={`min-h-[44px] px-4 py-2 rounded-full text-sm font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#7c3aed] focus-visible:ring-offset-2 ${format === f ? "bg-[#0b0f1a] text-white" : "bg-slate-100 text-slate-600"}`}>
              {t(`share.${f}`)} {sz && <span className="opacity-70 text-xs">{sz}</span>}
            </button>
          ))}
        </div>
        {(isJourney || isScore) && (
          <div className="flex gap-2 justify-center" role="group" aria-label={t("share.format_label")}>
            {[["post", "4:5"], ["story", "9:16"]].map(([s, r]) => (
              <button key={s} type="button" data-testid={`share-journey-size-${s}`} onClick={() => setJourneySize(s)} aria-pressed={journeySize === s}
                className={`min-h-[36px] px-3 rounded-full text-xs font-semibold border ${journeySize === s ? "border-[#0b0f1a] text-[#0b0f1a]" : "border-slate-200 text-slate-500"}`}>{t(`share.${s}`)} {r}</button>
            ))}
          </div>
        )}

        <div className="flex justify-center">
          <div className="relative rounded-2xl overflow-hidden border border-slate-200 bg-slate-50"
            style={{ width: size === "post" ? "min(260px, 60vw)" : "min(210px, 45vw)", aspectRatio: size === "post" ? "4/5" : "9/16" }} role="status" aria-live="polite" aria-busy={busy}>
            {busy || !preview ? (
              <div className="absolute inset-0 flex items-center justify-center"><Loader2 className="animate-spin text-[#7c3aed]" aria-label={t("share.generating")} /></div>
            ) : (
              <img src={preview} alt={`${isJourney ? t("share.journey_title") : t("share.card_myidea")}: ${project?.name || ""}`} data-testid="share-card-preview" className="w-full h-full object-cover" />
            )}
          </div>
        </div>

        <p className="text-xs text-slate-600 flex items-center gap-1.5 justify-center text-center" data-testid="share-private-note"><ShieldCheck size={12} aria-hidden="true" className="shrink-0" /> {isJourney ? t("share.journey_private_note") : t("share.private_note")}</p>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <Button data-testid="share-download" variant="outline" onClick={() => blob && downloadCard(blob, isJourney ? `journey-${journeySize}` : isScore ? `cpenz-score-${journeySize}` : format)} disabled={!blob} className="rounded-full h-12 font-semibold focus-visible:ring-2 focus-visible:ring-[#7c3aed]">
            <Download size={16} className="mr-2" aria-hidden="true" /> {t("share.download")}
          </Button>
          <Button data-testid="share-to-instagram" onClick={doShare} disabled={sharing || !blob} aria-busy={sharing}
            className="rounded-full h-12 text-white font-semibold focus-visible:ring-2 focus-visible:ring-[#0b0f1a]" style={{ background: "linear-gradient(90deg,#feda75,#d62976,#4f5bd5)" }}>
            {sharing ? <Loader2 size={16} className="animate-spin" /> : <><Instagram size={16} className="mr-2" aria-hidden="true" /> {t("share.to_instagram")}</>}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
